import { useState, useEffect, useMemo } from "react";
import { CompositeFilterDescriptor, FilterDescriptor, State } from "@progress/kendo-data-query";
import { debounce } from "lodash";

interface UseSearchFilterOptions {
  initialState: State;
  debounceMs?: number;
  searchFields?: string[];
}

interface UseSearchFilterReturn {
  searchValue: string;
  dataState: State;
  handleSearchChange: (value: string) => void;
  setDataState: (state: State) => void;
}

const isFilterDescriptor = (filter: CompositeFilterDescriptor | FilterDescriptor): filter is FilterDescriptor => {
  return "field" in filter && "operator" in filter;
};

const isCompositeFilter = (
  filter: CompositeFilterDescriptor | FilterDescriptor
): filter is CompositeFilterDescriptor => {
  return "logic" in filter && "filters" in filter;
};

export const useSearchFilter = ({
  initialState,
  debounceMs = 500,
  searchFields = ["name"],
}: UseSearchFilterOptions): UseSearchFilterReturn => {
  const [dataState, setDataState] = useState<State>(initialState);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
      }, debounceMs),
    [debounceMs]
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    
    debouncedSearch.cancel();
    
    if (!value || value.length === 0) {
      setDebouncedSearchValue("");
      setDataState(prevState => ({
        ...prevState,
        filter: {
          logic: "and",
          filters: prevState.filter?.filters?.filter(filter => {
            if (isCompositeFilter(filter)) {
              const firstSubFilter = filter.filters[0];
              return !(
                isFilterDescriptor(firstSubFilter) &&
                searchFields.includes(firstSubFilter.field as string) &&
                firstSubFilter.operator === 'contains'
              );
            }
            return true;
          }) || []
        } as CompositeFilterDescriptor
      }));
    } else {
      debouncedSearch(value);
    }
  };

  useEffect(() => {
    const newDataState: State = {
      ...dataState,
      filter: {
        logic: "and",
        filters: []
      } as CompositeFilterDescriptor
    };
  
    const existingFilters = dataState.filter?.filters?.filter(filter => {
      if (isCompositeFilter(filter)) {
        const firstSubFilter = filter.filters[0];
        return !(
          isFilterDescriptor(firstSubFilter) &&
          searchFields.includes(firstSubFilter.field as string) &&
          firstSubFilter.operator === 'contains'
        );
      }
      return true;
    }) || [];
  
    if (newDataState.filter) {
      newDataState.filter.filters = [...existingFilters];
  
      if (debouncedSearchValue) {
        const searchFilters = searchFields.map(field => ({
          field,
          operator: "contains",
          value: debouncedSearchValue
        }));
  
        newDataState.filter.filters.push({
          logic: "or",
          filters: searchFilters
        } as CompositeFilterDescriptor);
      }
    }
  
    setDataState(newDataState);
  }, [debouncedSearchValue]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return {
    searchValue,
    dataState,
    handleSearchChange,
    setDataState,
  };
};
